import { lazy, startTransition, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

import Suspense from "components/shared/Suspense";
import { Drawer } from "features/Drawer";
import { ButtonThemeProps } from "hardcover-ui/theme/components/button";
import { useApolloClient } from "lib/apollo/client";
import { updateUserBookRead } from "services/UserBookReadService";
import { setUserBookRead } from "store/books/booksSlice";
import EditionType from "types/EditionType";
import {
  BookSerializersBookByline,
  EditionSerializersEdition,
  UserBookSerializersUserBookDate,
} from "types/serializers";

import EditionSelectorButton from "./EditionSelectorButton";

const EditionSelectorDrawer = lazy(
  () => import("./EditionSelectorForUserBookReadDrawer"),
);
const EditEditionModalWrapper = lazy(() => import("./EditEditionModalWrapper"));

export default function EditionSelector({
  book,
  edition,
  children,
  size = "sm",
  userBookRead,
}: {
  book: BookSerializersBookByline;
  edition: EditionSerializersEdition;
  children?: React.ReactNode;
  size?: ButtonThemeProps["size"];
  userBookRead: UserBookSerializersUserBookDate;
}) {
  const client = useApolloClient();
  const buttonRef = useRef();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [newEditionFormOpen, setNewEditionFormOpen] = useState(false);
  const buttonClassName = useMemo(
    () => `edition-selector-button-${userBookRead.id}`,
    [userBookRead],
  );

  const editionChanged = (newEdition: EditionType) => {
    if (newEdition?.id && newEdition.id !== userBookRead.editionId) {
      return updateUserBookRead({
        client,
        id: userBookRead.id,
        object: {
          ...userBookRead,
          ...{ editionId: newEdition.id },
        },
      })
        .then(newUserBookRead => {
          dispatch(
            setUserBookRead({ bookId: book.id, userBookRead: newUserBookRead }),
          );
          if (newUserBookRead) {
            toast.success("Edition changed");
          }
        })
        .catch(error => {
          toast.error(`Error updating edition: ${error}`);
        });
    }
  };

  const closeEditEditionModal = () => {
    setNewEditionFormOpen(false);
  };

  const toggleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (event) {
      event.preventDefault();
    }

    setOpen(!open);
  };

  return (
    <div className="relative">
      <Suspense
        fallback={
          <EditionSelectorButton size={size} loading>
            {children}
          </EditionSelectorButton>
        }
      >
        <EditionSelectorButton
          className={buttonClassName}
          size={size}
          open={open}
          onClick={toggleOpen}
          ref={buttonRef}
        >
          {children}
        </EditionSelectorButton>

        {open && buttonRef?.current ? (
          <Suspense
            fallback={
              <Drawer.Loading
                settings={{
                  isOpen: open,
                  ref: buttonRef.current,
                  onClose: () => {},
                }}
              />
            }
          >
            <EditionSelectorDrawer
              edition={edition}
              book={book}
              userBookRead={userBookRead}
              onNewEdition={() => {
                startTransition(() => {
                  setOpen(false);
                  setNewEditionFormOpen(true);
                });
              }}
              settings={{
                isOpen: open,
                onClose: toggleOpen,
                ref: buttonRef.current,
              }}
            />
          </Suspense>
        ) : (
          false
        )}
      </Suspense>

      {newEditionFormOpen ? (
        <Suspense fallback={null}>
          <EditEditionModalWrapper
            book={book}
            onClose={closeEditEditionModal}
            onEditionChange={editionChanged}
          />
        </Suspense>
      ) : (
        false
      )}
    </div>
  );
}
